import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Page } from '../../layout/Page';
import { useAccountTimeZone } from '../../layout/ShellStatus';
import { Tabs } from '../../components/shared/Overlay';
import { ErrorState } from '../../components/shared/States';
import { reportsAPI } from '../../services/reportsApi';
import { useAuth } from '../../contexts/AuthContext';
import { ExportsTab } from './ExportsTab';
import { BuilderTab } from './BuilderTab';
import { SchedulesTab } from './SchedulesTab';
import { BoardTab } from './BoardTab';
import { PstnTab } from './PstnTab';
import { ScheduleDialog } from './ScheduleDialog';
import { TAB_IDS, TAB_PERMISSIONS, allowedTabs } from './reportsTabs';
import './reports.css';

const SUBTITLE = 'Export any view and build reports you can save and schedule.';

const settle = (promise) => promise.then((value) => ({ value }), (error) => ({ error }));

/**
 * Reports (docs/design/mockups/reports.html): Exports, Saved reports (builder), Schedules, Board summary and PSTN
 * reconciliation, deep-linked with #exports, #builder, #schedules, #board and #pstn.
 */
export function ReportsPage({ initialTab = 'exports' }) {
  useAccountTimeZone();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const allowed = allowedTabs(hasPermission);
  const hashTab = location.hash.replace('#', '');
  const requested = TAB_IDS.includes(hashTab) ? hashTab : initialTab;
  const tab = allowed.includes(requested) ? requested : allowed[0] || 'builder';
  const canManage = hasPermission(TAB_PERMISSIONS.schedules);

  const [datasets, setDatasets] = useState(null);
  const [saved, setSaved] = useState(null);
  const [schedules, setSchedules] = useState(null);
  const [schedulesError, setSchedulesError] = useState(null);
  const [recipientDirectory, setRecipientDirectory] = useState(null);
  const [delivery, setDelivery] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [scheduleDraft, setScheduleDraft] = useState(null);

  const reloadSaved = useCallback(() => reportsAPI.getSaved().then(setSaved, () => setSaved([])), []);
  const reloadSchedules = useCallback(() => {
    reportsAPI.getRecipientGroups().then(setRecipientDirectory, () => setRecipientDirectory(null));
    return reportsAPI.getSchedules().then(
      (list) => {
        setSchedules(list);
        setSchedulesError(null);
      },
      (error) => {
        setSchedules([]);
        setSchedulesError(error);
      }
    );
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    Promise.all([settle(reportsAPI.getDatasets()), settle(reportsAPI.getDeliveryStatus())]).then(([ds, status]) => {
      if (cancelled) return;
      if (ds.error) setLoadError(ds.error);
      else setDatasets(ds.value);
      if (status.value) setDelivery(status.value);
    });
    reloadSaved();
    if (canManage) reloadSchedules();
    else setSchedules([]);
    return () => {
      cancelled = true;
    };
  }, [reloadKey, reloadSaved, reloadSchedules, canManage]);

  const selectTab = (id) => navigate({ pathname: location.pathname, hash: id }, { replace: true });

  const tabs = [
    { id: 'exports', label: 'Exports' },
    { id: 'builder', label: 'Saved reports', count: saved?.length || null },
    { id: 'schedules', label: 'Schedules', count: schedules?.length || null },
    { id: 'board', label: 'Board summary' },
    { id: 'pstn', label: 'PSTN reconciliation' },
  ].filter((t) => allowed.includes(t.id));

  const openSchedule = (draft) => setScheduleDraft(draft);

  let content;
  if (loadError) {
    content = (
      <ErrorState
        title="Reports couldn't load"
        body="The reporting service didn't respond. Scheduled reports are unaffected and will still render on time."
        error={loadError}
        onRetry={() => setReloadKey((k) => k + 1)}
      />
    );
  } else if (tab === 'exports') {
    content = <ExportsTab datasets={datasets} />;
  } else if (tab === 'builder') {
    content = (
      <BuilderTab
        datasets={datasets}
        saved={saved}
        schedules={schedules}
        onSavedChanged={reloadSaved}
        canManage={canManage}
        canExport={hasPermission(TAB_PERMISSIONS.exports)}
        onSchedule={(report) => openSchedule({ savedReportId: report.id, name: report.name, format: report.template === 'CommunityMonthlySummary' ? 'Pdf' : 'Xlsx' })}
      />
    );
  } else if (tab === 'schedules') {
    content = (
      <SchedulesTab
        schedules={schedules}
        directory={recipientDirectory}
        error={schedulesError}
        delivery={delivery}
        onNew={() => openSchedule({})}
        onEdit={(schedule) => openSchedule(schedule)}
        onChanged={reloadSchedules}
      />
    );
  } else if (tab === 'board') {
    content = <BoardTab onScheduleMonthly={(draft) => openSchedule(draft)} />;
  } else {
    content = <PstnTab />;
  }

  return (
    <Page title="Reports" subtitle={SUBTITLE}>
      <Tabs tabs={tabs} value={tab} onChange={selectTab} label="Reports" />
      {content}
      <ScheduleDialog
        draft={scheduleDraft}
        saved={saved || []}
        delivery={delivery}
        directory={recipientDirectory}
        onClose={() => setScheduleDraft(null)}
        onSaved={() => {
          setScheduleDraft(null);
          reloadSaved();
          reloadSchedules();
        }}
      />
    </Page>
  );
}

export function ReportBuilderPage() {
  return <ReportsPage initialTab="builder" />;
}
